/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Page SubscriberId Products Tab component
 *
 * @author Yakov Gushchin
 * @version 1.19.0
 * @since 1.12.0
 */

import { Fragment, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Skeleton } from '../../../../../components/common';
import { AccessComponentKeyEnum } from '../../../../../modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '../../../../../modules/role/selectors/guardSelector';
import { wizardStorageClearStepsAction } from '../../../../../modules/wizard/wizardActions';
import { EWizardName } from '../../../../../modules/wizard/wizardConstants';
import { openRoute } from '../../../../../routes/constants/externalRoutes';
import { CLIENT_ID, RoutePath } from '../../../../../routes/constants/routesConstants';
import { productsStartWizardFromSubscriberIdAction } from '../../../../PageProducts/tariffsAndServicesActions';
import { useSubscriberIdContext } from '../../../context';
import { subscriberIdRegionIdSelector, subscriberIdServiceCategoryIdSelector } from '../../../selectors';
import SubscriberIdError from '../../SubscriberIdError';
import { makeSelectorProductTranslation } from '../selectors';
import { useGetProductListQuery } from '../store';
import { IProductItem } from '../types';
import {
    ProductCardCategoryStyled,
    ProductCardListStyled,
    ProductCardTabsStyled,
    ProductCardTabStyled,
} from './styled/SubscriberIdProductStyled';
import SubscriberIdProductCard from './SubscriberIdProductCard';
import SubscriberIdProductsSkeleton from './SubscriberIdProductsSkeleton';

const SubscriberIdProducts = () => {

    const [currentTabIndex, setCurrentTabIndex] = useState(0);

    const dispatch = useDispatch();

    const isProductEnabled = useSelector(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageProducts));
    const tabTranslation = useSelector(makeSelectorProductTranslation());

    const { subscriberId, userType } = useSubscriberIdContext();

    const regionId = useSelector(subscriberIdRegionIdSelector(subscriberId, userType));
    const servicesCategoryId = useSelector(subscriberIdServiceCategoryIdSelector(subscriberId, userType));

    const { data, error, isFetching, isSuccess, refetch } = useGetProductListQuery({
        subscriberId,
        servicesCategoryId: servicesCategoryId || -1,
    }, { skip: !servicesCategoryId });

    const onDisconnect = ({ id }: IProductItem) => {
        if (!isProductEnabled) return;

        // Clear previous master
        dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.PRODUCT_OFFERINGS }));

        // Add current product to page product reducer
        dispatch(productsStartWizardFromSubscriberIdAction({ id, regionId: regionId || -1 }));

        // start master
        const url = RoutePath.wizardProductOfferingsFromSubscriberId.replace(CLIENT_ID, subscriberId);
        openRoute(url);
    };

    if (error || (isSuccess && !data)) return <SubscriberIdError refetch={refetch}/>;
    if (isFetching) return <Skeleton isLoading count={2} render={SubscriberIdProductsSkeleton}/>;
    if (!data || !data.length) return <div className="subscriber-id__title subscriber-id__title_bold">
        {tabTranslation.empty}
    </div>;

    return (
        <Fragment>
            <ProductCardTabsStyled>
                {
                    data.map((data, index) => data.count !== 0 && (
                        <ProductCardTabStyled
                            isActive={index === currentTabIndex}
                            key={data.title}
                            onClick={() => setCurrentTabIndex(index)}
                        >
                            {data.title}
                            <span className="counter">{data.count}</span>
                        </ProductCardTabStyled>
                    ))
                }
            </ProductCardTabsStyled>

            {
                !!data[currentTabIndex]?.products?.length && (
                    <ProductCardListStyled>
                        {
                            data[currentTabIndex]?.products?.map((product) =>
                                <SubscriberIdProductCard
                                    key={`${product.title}-${product.id}`}
                                    product={product}
                                    onDisconnect={() => onDisconnect(product)}
                                />)
                        }
                    </ProductCardListStyled>
                )
            }

            {
                data[currentTabIndex]?.children?.map((childProducts) =>
                    !!childProducts.products?.length && (
                        <ProductCardCategoryStyled key={childProducts.title}>
                            <div className="product-card__category-title">
                                {childProducts.title} ({childProducts.products.length})
                            </div>
                            <ProductCardListStyled>
                                {childProducts.products?.map(product =>
                                    <SubscriberIdProductCard
                                        key={`${product.title}-${product.id}`}
                                        product={product}
                                        onDisconnect={() => onDisconnect(product)}
                                    />)}
                            </ProductCardListStyled>
                        </ProductCardCategoryStyled>
                    ))
            }
        </Fragment>
    );
};

export default SubscriberIdProducts;
